import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { snakeCase } from 'lodash';
import { Order, OrderStatus, OrderType } from './types';

export interface MerchantOrderFilters {
  createdFrom: Date | null;
  createdTo: Date | null;
  completedFrom: Date | null;
  completedTo: Date | null;
  externalStatuses?: OrderStatus[] | null;
  hadExternalStatuses?: OrderStatus[] | null;
  orderOrPreviewId: string | null;
  referenceId: string | null;
  orderType: OrderType | null;
  errors: boolean | null;
  abandonedAfterBuy: boolean | null;
  merchantId?: string | null;
  hasFailedCallback: boolean | null;
}

export interface Pagination {
  page: number;
  size: number;
}

export interface SortOptions {
  field:
    | 'ORDER_ID'
    | 'REFERENCE_ID'
    | 'CREATED_AT'
    | 'COMPLETED_AT'
    | 'AMOUNT'
    | 'STATUS'
    | 'SETTLEMENT_AMOUNT'
    | 'PROCESSING_FEE'
    | 'SETTLEMENT_TOTAL_AMOUNT';
  ascending: boolean;
}

export interface MerchantTotalOrderAggregatedData {
  orderCount: number;
  settlementAmount: number;
  processingFee: number;
}

export interface MerchantOrderResponse {
  orders: Order[];
  aggregatedData: MerchantTotalOrderAggregatedData;
}

export const getOrders = async ({
  pagination,
  filters,
  sortOptions,
}: {
  pagination?: Pagination;
  filters?: MerchantOrderFilters;
  sortOptions?: SortOptions;
}): Promise<MerchantOrderResponse> => {
  const convertedFilters = filters && {
    ...filters,
    completedFrom: filters.completedFrom
      ? filters.completedFrom.toISOString()
      : null,
    completedTo: filters.completedTo ? filters.completedTo.toISOString() : null,
    createdFrom: filters.createdFrom ? filters.createdFrom.toISOString() : null,
    createdTo: filters.createdTo ? filters.createdTo.toISOString() : null,
  };
  const data = await fetchAPI<MerchantOrderResponse>(`v1/orders:search`, {
    method: 'POST',
    body: {
      pagination,
      filters: convertedFilters,
      sortOptions,
    },
  });

  return {
    ...data,
    orders: data.orders.map(
      ({ completedAt, createdAt, errors, callbacks, ...rest }) => ({
        completedAt: completedAt ? new Date(completedAt) : null,
        createdAt: new Date(createdAt),
        errors: errors.map((e) => ({ ...e, createdAt: new Date(e.createdAt) })),
        callbacks: callbacks.map((c) => ({
          ...c,
          requestBody: Object.keys(c.requestBody).reduce(
            (acc, key) => ({ ...acc, [snakeCase(key)]: c.requestBody[key] }),
            {}
          ),
          createdAt: new Date(c.createdAt),
        })),
        ...rest,
      })
    ),
  };
};

export const createOrder = ({
  amount,
  currency,
  referenceId,
}: {
  amount: number;
  currency: string;
  referenceId?: string;
}) => {
  const body: {
    amount: number;
    currency: string;
    referenceId?: string;
  } = {
    amount,
    currency,
    referenceId,
  };
  return fetchAPI<Order>('v1/orders', {
    method: 'POST',
    body: body,
  });
};

type MerchantBOLayoutMode = 'ORDERS' | 'WALLET';

export interface Config {
  amountToleranceLowerBound: number;
  amountToleranceUpperBound: number;
  minutesToCancelOrderAfter: number;
  cancelOrderOnError: boolean;
  retryOrderCallbacks: boolean;
  useWalletRecipientDuality: boolean;
  allowBackofficeOrderCreation?: boolean; // undefined because read only
  allowCorporateWallet?: boolean; // undefined because read only
  layoutMode: MerchantBOLayoutMode;
}

export type Status = 'ACTIVE' | 'BLOCKED';

export interface MerchantConfig {
  config: Config;
  status: Status;
  displayName: string;
}

export interface MerchantConfigUpdateInput {
  config?: Config;
  status?: Status;
  displayName?: string;
}

// Convert decimal tolerance values from backend to percentage for display
const convertConfigFromBackend = (data: MerchantConfig): MerchantConfig => ({
  ...data,
  config: {
    ...data.config,
    amountToleranceLowerBound: data.config.amountToleranceLowerBound * 100,
    amountToleranceUpperBound: data.config.amountToleranceUpperBound * 100,
  },
});

// Convert percentage tolerance values from frontend to decimal for backend
const convertConfigToBackend = (
  input: MerchantConfigUpdateInput
): Record<string, unknown> => {
  if (!input.config) {
    return { ...input };
  }
  return {
    ...input,
    config: {
      ...input.config,
      amountToleranceLowerBound: input.config.amountToleranceLowerBound / 100,
      amountToleranceUpperBound: input.config.amountToleranceUpperBound / 100,
    },
  };
};

export const getConfig = async () => {
  const data = await fetchAPI<MerchantConfig>(`v1/config`, {
    method: 'GET',
  });

  return convertConfigFromBackend(data);
};

export const updateConfig = async (config: MerchantConfigUpdateInput) => {
  const data = await fetchAPI<MerchantConfig>(`v1/config`, {
    method: 'PUT',
    body: convertConfigToBackend(config),
  });

  return convertConfigFromBackend(data);
};
